/**
 * Cookie utility functions for handling referral tracking
 */

/**
 * Parse cookie header string into key-value pairs
 */
export function parseCookies(cookieHeader: string): Record<string, string> {
  const cookies: Record<string, string> = {};
  if (!cookieHeader) return cookies;

  cookieHeader.split(';').forEach((cookie) => {
    const [key, value] = cookie.trim().split('=');
    if (key && value) {
      cookies[key] = decodeURIComponent(value);
    }
  });

  return cookies;
}

/**
 * Get a specific cookie value from cookie header
 */
export function getCookieFromHeader(
  cookieHeader: string,
  name: string,
): string | null {
  const cookies = parseCookies(cookieHeader);
  return cookies[name] || null;
}

/**
 * Client-side: Get cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (const cookie of ca) {
    const c = cookie.trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Client-side: Set cookie with expiration
 */
export function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;

  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = '; expires=' + date.toUTCString();

  // Set cookie on .aurium.ai domain for cross-subdomain access
  const domain = window.location.hostname.includes('aurium.ai')
    ? '; domain=.aurium.ai'
    : '';

  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/${domain}; SameSite=Lax`;
}

/**
 * Client-side: Get all cookie values by name (handles multiple cookies with same name)
 */
export function getAllCookies(name: string): string[] {
  if (typeof document === 'undefined') return [];

  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  const values: string[] = [];

  for (const cookie of ca) {
    const c = cookie.trim();
    if (c.indexOf(nameEQ) === 0) {
      const value = decodeURIComponent(c.substring(nameEQ.length));
      // Only add unique values
      if (!values.includes(value)) {
        values.push(value);
      }
    }
  }
  return values;
}

/**
 * Client-side: Delete cookie
 */
export function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;

  const domain = window.location.hostname.includes('aurium.ai')
    ? '; domain=.aurium.ai'
    : '';

  document.cookie = `${name}=; Max-Age=-99999999; path=/${domain}`;
}
